import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { createBloodPressureMeasurement } from "~/api/bloodPressure";
import { errorToast, useToastStore } from "~/components/Shared/Toast";
import { Button, Input, Label, Modal } from "~/ui";

interface BloodPressureInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
}

export const BloodPressureInputModal = ({
  isOpen,
  onClose,
  patientId,
}: BloodPressureInputModalProps) => {
  useTranslation();
  const { pushToast } = useToastStore();
  const queryClient = useQueryClient();

  // Helper function to get local datetime string for input
  const getLocalDateTimeString = (date: Date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    systolic: "120",
    diastolic: "80",
    measured_at: getLocalDateTimeString(), // Format: YYYY-MM-DDTHH:MM in local timezone
  });

  const { mutate: createMeasurement, isPending } = useMutation({
    mutationFn: createBloodPressureMeasurement,
    onSuccess: async () => {
      // Invalidate relevant queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["getPatient", { id: patientId }],
      });
      await queryClient.invalidateQueries({
        queryKey: ["lastMeasurements", patientId],
      });

      // Show success toast
      void pushToast({
        type: "success",
        message: "Blood pressure measurement added successfully!",
      });

      // Reset form
      setFormData({
        systolic: "",
        diastolic: "",
        measured_at: getLocalDateTimeString(),
      });

      // Close modal after a short delay to ensure toast is visible
      setTimeout(() => {
        onClose();
      }, 1000);
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const systolic = parseFloat(formData.systolic);
    const diastolic = parseFloat(formData.diastolic);

    if (isNaN(systolic) || systolic < 50 || systolic > 200) {
      void pushToast({
        type: "error",
        message: "Systolic pressure must be between 50 and 200 mmHg",
      });
      return;
    }

    if (isNaN(diastolic) || diastolic < 30 || diastolic > 150) {
      void pushToast({
        type: "error",
        message: "Diastolic pressure must be between 30 and 150 mmHg",
      });
      return;
    }

    if (systolic <= diastolic) {
      void pushToast({
        type: "error",
        message: "Systolic pressure must be higher than diastolic pressure",
      });
      return;
    }

    // Convert local datetime to the expected format (YYYY-MM-DDTHH:mm:ssZ)
    // The input is in local timezone, convert to UTC for API
    const localDate = new Date(formData.measured_at);
    const measuredAt = localDate.toISOString().replace(/\.\d{3}Z$/, "Z");

    createMeasurement({
      patient_id: patientId,
      systolic,
      diastolic,
      measured_at: measuredAt,
    });
  };

  const handleReset = () => {
    setFormData({
      systolic: "120",
      diastolic: "80",
      measured_at: getLocalDateTimeString(),
    });
  };

  const isFormValid = () => {
    const systolic = parseFloat(formData.systolic);
    const diastolic = parseFloat(formData.diastolic);
    const isValid =
      !isNaN(systolic) &&
      !isNaN(diastolic) &&
      systolic >= 50 &&
      systolic <= 200 &&
      diastolic >= 30 &&
      diastolic <= 150 &&
      systolic > diastolic &&
      formData.systolic.trim() !== "" &&
      formData.diastolic.trim() !== "";
    return isValid;
  };

  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  return (
    <Modal
      title="Add Blood Pressure"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="systolic"
              className="text-sm font-medium text-gray-200"
            >
              Systolic (mmHg) - Range: 50-200
            </Label>
            <Input
              id="systolic"
              type="number"
              step="1"
              min="50"
              max="200"
              value={formData.systolic}
              onChange={handleInputChange("systolic")}
              placeholder="120"
              className="mt-1"
              required
            />
          </div>

          <div>
            <Label
              htmlFor="diastolic"
              className="text-sm font-medium text-gray-200"
            >
              Diastolic (mmHg) - Range: 30-150
            </Label>
            <Input
              id="diastolic"
              type="number"
              step="1"
              min="30"
              max="150"
              value={formData.diastolic}
              onChange={handleInputChange("diastolic")}
              placeholder="80"
              className="mt-1"
              required
            />
          </div>
        </div>

        <div>
          <Label
            htmlFor="measured_at"
            className="text-sm font-medium text-gray-200"
          >
            Measured At
          </Label>
          <Input
            id="measured_at"
            type="datetime-local"
            value={formData.measured_at}
            onChange={handleInputChange("measured_at")}
            className="mt-1"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            text="Reset"
            color="secondary"
            onClick={handleReset}
            disabled={isPending}
          />
          <Button
            type="submit"
            text={isPending ? "Saving..." : "Save"}
            color="primary"
            disabled={isPending || !isFormValid()}
          />
        </div>
      </form>
    </Modal>
  );
};
